// src/components/FinancialTicker.js - VERSION AFFICHANT TOUS LES ACTIFS

import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './FinancialTicker.css';

const FinancialTicker = () => {
    const [tickers, setTickers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false); // État pour toggle
    const socketRef = useRef(null);

    useEffect(() => {
        console.log('📡 Initialisation FinancialTicker...');
        
        const initSocketIO = () => {
            const socket = io('https://api.blackroq.pro', {
                transports: ['websocket', 'polling'],
                reconnection: true,
                reconnectionAttempts: 5,
                timeout: 10000
            });

            socketRef.current = socket;

            socket.on('connect', () => {
                console.log('✅ Socket.IO connecté pour ticker');
                setError(null);
            });

            socket.on('connect_error', (err) => {
                console.error('❌ Erreur connexion Socket:', err.message);
                fetchTickersViaAPI();
            });

            socket.on('tickerData', (data) => {
                console.log(`📊 ${data.length} tickers reçus via Socket.IO`);
                if (data && data.length > 0) {
                    processTickersData(data);
                } else {
                    fetchTickersViaAPI();
                }
            });

            socket.on('disconnect', () => {
                console.log('⚠️ Socket.IO déconnecté');
            });

            // Timeout de sécurité
            setTimeout(() => {
                if (loading) {
                    console.log('⏰ Timeout Socket.IO, tentative API...');
                    fetchTickersViaAPI();
                }
            }, 5000);
        };

        const fetchTickersViaAPI = async () => {
            try {
                console.log('🔄 Tentative via API REST...');
                const response = await fetch('https://api.blackroq.pro/api/tickers');
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                
                const data = await response.json();
                console.log(`✅ ${data.length} tickers chargés via API`);
                processTickersData(data);
                
            } catch (err) {
                console.error('❌ Erreur API:', err.message);
                setError('Impossible de charger les données du marché');
                setLoading(false);
            }
        };

        const processTickersData = (data) => {
            if (data && Array.isArray(data) && data.length > 0) {
                const validTickers = data.filter(ticker => 
                    ticker && 
                    ticker.symbol && 
                    typeof ticker.price === 'number' &&
                    typeof ticker.changePercent === 'number'
                );
                
                if (validTickers.length > 0) {
                    setTickers(validTickers);
                    setLoading(false);
                    setError(null);
                } else {
                    setError('Format de données invalide');
                    setLoading(false);
                }
            } else {
                setError('Aucune donnée disponible');
                setLoading(false);
            }
        };

        initSocketIO();

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    // Rendu du ticker avec TOUS les actifs
    const renderTickerTape = () => {
        if (tickers.length === 0) return null;
        
        // Filtrer et organiser les tickers
        let displayTickers;
        
        if (showAll) {
            // Afficher TOUS les tickers
            displayTickers = [...tickers];
        } else {
            // Afficher seulement les plus actifs (par volatilité)
            const sortedTickers = [...tickers].sort((a, b) => 
                Math.abs(b.changePercent) - Math.abs(a.changePercent)
            );
            displayTickers = sortedTickers.slice(0, 12); // 12 actifs max en mode réduit
        }
        
        // Créer un tableau 3x pour défilement continu
        const tapeTickers = [...displayTickers, ...displayTickers, ...displayTickers];
        
        return (
            <div className="ticker-tape">
                {tapeTickers.map((ticker, index) => (
                    <div key={`${ticker.symbol}-${index}`} className="ticker-item">
                        <span className="ticker-symbol">{ticker.symbol}</span>
                        <span className="ticker-price">
                            ${ticker.price.toFixed(2)}
                        </span>
                        <span className={`ticker-change ${ticker.changePercent >= 0 ? 'text-success' : 'text-danger'}`}>
                            {ticker.changePercent >= 0 ? '▲' : '▼'} 
                            {Math.abs(ticker.changePercent).toFixed(2)}%
                        </span>
                        <span className="ticker-divider">|</span>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="financial-ticker-container shadow-sm">
                <div className="ticker-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <i className="bi bi-graph-up me-2"></i>
                        Marché en temps réel
                    </h6>
                </div>
                <div className="ticker-content">
                    <div className="ticker-loading">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>Connexion au marché...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="financial-ticker-container shadow-sm">
                <div className="ticker-header">
                    <h6 className="mb-0">
                        <i className="bi bi-graph-up me-2"></i>
                        Marché en temps réel
                    </h6>
                </div>
                <div className="ticker-content">
                    <div className="ticker-error">
                        <small>{error}</small>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="financial-ticker-container shadow-sm">
            <div className="ticker-header d-flex justify-content-between align-items-center">
                <div>
                    <h6 className="mb-0">
                        <i className="bi bi-graph-up me-2"></i>
                        Marché en temps réel
                    </h6>
                    <small className="d-block mt-1">
                        {showAll ? 'Tous les actifs' : `${tickers.length} actifs disponibles`}
                    </small>
                </div>
                <button 
                    className="btn btn-sm btn-outline-light"
                    onClick={() => setShowAll(!showAll)}
                >
                    {showAll ? 'Réduire' : 'Voir tout'}
                </button>
            </div>
            
            <div className="ticker-content">
                {renderTickerTape()}
            </div>
            
            <div className="ticker-footer d-flex justify-content-between align-items-center">
                <div>
                    <small>
                        <i className="bi bi-lightning-charge me-1"></i>
                        {showAll ? `${tickers.length} actifs` : 'Top actifs'}
                    </small>
                </div>
                <div>
                    <small>
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Mise à jour automatique
                    </small>
                </div>
            </div>
        </div>
    );
};

export default FinancialTicker;